import React, { useState } from 'react'
import styled from 'styled-components'
import { Column, Row } from 'components/common/layout'
import { Modal } from 'components/common/modal'
import { BaseText, DescText, SemiBoldText } from 'components/common/text'
import { FiChevronRight } from 'react-icons/fi'
import { theme } from 'infra/theme'
import { ScoutProfile, WithIdLink } from 'types/common'
import { observer } from 'mobx-react'
import { useIsDesktop } from 'utils/hooks'
import { FileUploader } from 'components/scouts/profile/common/file-uploader'
import { BtnType, Button } from 'components/common/button'
import { requestToast } from 'components/common/toast'
import {
  postRequestScoutRequestMeInfoFill,
  postRequestScoutRequestMeSendBackLink,
  putRequestScoutRequestMe,
} from 'apis/request'
import { mutate } from 'swr'
import { analytics } from 'infra/analytics'
import { useRouter } from 'next/router'
import { LinkUploader } from 'components/apply/link-uploader'
import { useScoutMyRequest } from 'apis/hooks'
import { initialRequestForm } from 'types/initial'
import { RoutePaths, RouterQueryKey } from 'types/router'
import { HIDE_PROFILE_FILL_NEED_MODAL_KEY } from 'infra/constants'

const SendLinkModal: React.FC<{ onClose: () => void; onSuccess: () => void }> =
  observer(({ onClose, onSuccess }) => {
    return (
      <Modal width={440} onClose={onClose} disableCloseBtn>
        <ModalContent style={{ gap: 24 }}>
          <SubModalMainText>
            {
              '이력서 파일/링크를 찾고 돌아오기 쉽게\n카카오톡으로 링크를 보내드릴게요'
            }
          </SubModalMainText>
          <ModalExplainText>
            이 링크로 접속하시면 어느 기기로 들어오든 자동 로그인되어요.
          </ModalExplainText>
          <Column style={{ gap: 8, margin: '0 auto' }}>
            <ModalButton onClick={onSuccess}>
              <ButtonIconWrapper>
                카카오톡으로 링크 받기
                <FiChevronRight
                  size={16}
                  color={theme.color.white1}
                  strokeWidth={3}
                />
              </ButtonIconWrapper>
            </ModalButton>
            <ModalButton variant={BtnType.PRIMARY2} onClick={onClose}>
              <ButtonIconWrapper>
                지금 바로 AI로 프로필 정보 가져오기
                <FiChevronRight
                  size={16}
                  color={theme.color.primary4}
                  strokeWidth={3}
                />
              </ButtonIconWrapper>
            </ModalButton>
          </Column>
        </ModalContent>
      </Modal>
    )
  })

export const ProfileFillRequestModal: React.FC<{
  onClose: () => void // 닫기 버튼 클릭 시에도 공통으로 사용되므로 이 모달을 닫는 동작만 이 prop으로 전달
  onSuccess?: () => void // 대신 작성 요청 완료 후, 모달 닫는 것 외에 페이지 이동 등 추가로 필요한 로직이 있을 경우
  files?: WithIdLink[]
  links?: string[]
  hasTossLinkModal?: boolean
}> = observer(
  ({
    onClose,
    onSuccess,
    files: _files,
    links: _links,
    hasTossLinkModal = true,
  }) => {
    const { data: profile } = useScoutMyRequest()
    const router = useRouter()
    const isDesktop = useIsDesktop()
    const [isTossLinkModalOpen, setIsTossLinkModalOpen] = useState(false)
    const [mainFile, setMainFile] = useState<number | null>(null)
    const [files, setFiles] = useState<WithIdLink[]>(_files || [])
    const [links, setLinks] = useState<string[]>([...(_links || []), '']) // link는 항상 여분의 빈값을 남겨둠
    const [isLoading, setIsLoading] = useState(false)

    const submit = async () => {
      if (isLoading) return
      setIsLoading(true)
      try {
        if (new Set(links).size !== links.length) {
          throw '중복된 링크를 제거해주세요.'
        }
        if (files.length === 0 && links.length === 1)
          throw '파일과 링크 중 하나 이상 올려주세요.'
        if (files.length > 1 && !mainFile) throw '대표 파일을 선택해주세요.'
        const savedLinks = profile?.links.map((_item) => _item.link) || []
        const savedFiles = profile?.files.map((item) => item.id) || []
        const addedLinks = links.filter(
          (item) => item.trim() !== '' && !savedLinks.includes(item),
        ) // 실제 저장할 링크들
        const addedFiles = files.filter((item) => !savedFiles.includes(item.id))
        const profilePayload = {
          files: [...savedFiles, ...addedFiles.map((item) => item.id)],
          addedLinks,
        }
        await putRequestScoutRequestMe(
          profile
            ? profilePayload
            : { ...initialRequestForm, ...profilePayload }, // 프로필이 없을 경우, 기본 데이터 생성해주기 위해 initialRequestForm 포함
        )
        const res = await mutate('/scouts/requests/me')
        const data = res.data as ScoutProfile
        const _mainFile =
          files.length > 1 ? mainFile : files.length === 1 ? files[0].id : null
        const infoFillPayload = {
          mainFile: _mainFile,
          files: files
            .filter((item) => item.id !== _mainFile) // 메인파일을 제외하고 보내야함
            .map((item) => item.id),
          links: data.links
            .filter((item) => links.includes(item.link))
            .map((item) => item.id),
        }
        await requestToast(
          postRequestScoutRequestMeInfoFill(infoFillPayload).then(() => {
            mutate('/scouts/requests/me')
            mutate('/scouts/requests/me/info-fill-status')
          }),
          {
            loading: '요청중',
            success:
              '이력서를 전달했어요!\n그룹바이가 정보를 작성하고 카카오톡 알림을 드릴게요. (최대 2일 소요)',
          },
          { icon: '🎉', duration: 8000 },
        )
        localStorage.setItem(HIDE_PROFILE_FILL_NEED_MODAL_KEY, 'false')
        analytics.track('click_groupby_toss_submit', {
          path: router.asPath,
          isMyProfile: router.asPath.includes('my-profile'),
        })
        onSuccess?.()
        onClose()
      } catch (e) {
        alert(e)
      }
      setIsLoading(false)
    }

    return (
      <>
        <Modal width={500} onClose={onClose} disableBackgroundClick>
          <ModalContent>
            <ModalSection>
              <ModalMainText>
                {`그룹바이가 대신\n프로필을 완성해 드릴게요`}
              </ModalMainText>
              <CustomModalExplainText>
                {`준비하신 이력서, 경력기술서, 자기소개서를\n바탕으로 프로필을 작성해요`}
              </CustomModalExplainText>
              {hasTossLinkModal && (
                <ModalTextButton onClick={() => setIsTossLinkModalOpen(true)}>
                  지금 당장 파일이나 링크를 가져올 수 없다면?{' '}
                  <FiChevronRight size={isDesktop ? 24 : 18} />
                </ModalTextButton>
              )}
            </ModalSection>
            <Column style={{ gap: 56 }}>
              <ModalSection>
                <Column style={{ gap: 8 }}>
                  <ModalSectionTitle>파일</ModalSectionTitle>
                  <AttachmentSubText>
                    자기소개, 경력 및 활동을 확인할 수 있는 파일을 업로드해
                    주세요
                  </AttachmentSubText>
                </Column>
                <Column style={{ gap: 8 }}>
                  {files.length > 1 && (
                    <BaseText>대표 파일을 선택해 주세요</BaseText>
                  )}
                  <FileUploader
                    files={files}
                    isSelectable={files.length > 1}
                    selected={mainFile}
                    onSelect={(fileId) => setMainFile(fileId)}
                    onChange={(file) => setFiles([...files, file])}
                    onRemove={(fileId) => {
                      setFiles(files.filter((item) => item.id !== fileId))
                      if (fileId === mainFile) setMainFile(null)
                    }}
                  />
                </Column>
              </ModalSection>
              <ModalSection>
                <Column style={{ gap: 8 }}>
                  <ModalSectionTitle>링크</ModalSectionTitle>
                  <AttachmentSubText>
                    노션, 링크드인, 로켓펀치 등 퍼블릭 엑세스가 가능한 링크를
                    입력해 주세요
                  </AttachmentSubText>
                </Column>
                <LinkUploader links={links} setLinks={setLinks} />
              </ModalSection>
            </Column>
            <ModalButton onClick={submit}>
              AI로 프로필 정보 가져오기
            </ModalButton>
          </ModalContent>
        </Modal>
        {isTossLinkModalOpen && (
          <SendLinkModal
            onClose={() => setIsTossLinkModalOpen(false)}
            onSuccess={async () => {
              try {
                const isRequestForm = router.asPath.includes('request-form')
                await requestToast(
                  postRequestScoutRequestMeSendBackLink(),
                  {
                    loading: '요청중',
                    success: `카카오톡으로 링크를 보냈어요!${
                      isRequestForm
                        ? ''
                        : '\n파일/링크를 찾은 후, 이 링크로 돌아와주세요.'
                    }`,
                  },
                  { duration: 5000 },
                )
                if (isRequestForm)
                  router.push({
                    pathname: RoutePaths.REQUEST_FORM_TOSS,
                    query: { [RouterQueryKey.HAS_SENT_LINK]: 'true' },
                  })
                await mutate('/scouts/requests/me/info-fill-status')
                onClose()
              } catch (e) {
                alert(e)
              }
            }}
          />
        )}
      </>
    )
  },
)

const ModalContent = styled(Column)`
  padding: 48px 24px;
  gap: 48px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 32px 20px;
    gap: 32px;
  }
`

const ModalMainText = styled(SemiBoldText)`
  text-align: center;
  font-size: ${(p) => p.theme.fontSize.h3};
  line-height: 36px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    line-height: 28px;
    font-size: ${(p) => p.theme.fontSize.h4};
  }
`

const ModalExplainText = styled(DescText)`
  text-align: center;
  color: ${(p) => p.theme.color.gray1};
  font-weight: ${(p) => p.theme.fontWeight.regular};
  @media ${(p) => p.theme.deviceSize.mobile} {
    line-height: 20px;
    font-size: ${(p) => p.theme.fontSize.default};
  }
`

const ModalButton = styled(Button)`
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 12px;
  }
`

const ModalTextButton = styled(SemiBoldText)`
  display: flex;
  justify-content: center;
  font-size: ${(p) => p.theme.fontSize.h5};
  color: ${(p) => p.theme.color.secondary};
  cursor: pointer;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.default};
  }
`

const ModalSection = styled(Column)`
  gap: 16px;
`

const ModalSectionTitle = styled(SemiBoldText)`
  font-size: ${(p) => p.theme.fontSize.h5};
`

const AttachmentSubText = styled(BaseText)`
  color: ${(p) => p.theme.color.gray2};
  font-weight: ${(p) => p.theme.fontWeight.regular};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.small};
  }
`

const CustomModalExplainText = styled(ModalExplainText)`
  @media ${(p) => p.theme.deviceSize.desktop} {
    white-space: nowrap;
  }
`

const ButtonIconWrapper = styled(Row)`
  gap: 10px;
  justify-content: center;
`

const SubModalMainText = styled(SemiBoldText)`
  text-align: center;
  font-size: ${(p) => p.theme.fontSize.h4};
  line-height: 1.4;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.h5};
  }
`
